import React, { useState, useEffect } from 'react';
import { Button, Box, Snackbar, Alert, Divider, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Select, MenuItem, InputLabel, FormControl, Switch, FormControlLabel } from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import getHelper from '../helpers/getHelper';
import postHelper from '../helpers/postHelper';
import configs from '../helpers/configs';

export default function DoRandomDeal() {
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [scalperBotActive, setScalperBotActive] = useState('')
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogValues, setDialogValues] = useState({});

  const { baseUrl } = configs;

  useEffect(() => {
    const fetchScalperBotState = async () => {
      try {
        const [scalperBotRes, settings] = await Promise.all([
          getHelper(`${baseUrl}/botstatus?rule=scalperbot`),
          getHelper(`${baseUrl}/settings?setting=scalper`)
        ]);
        const {state} = scalperBotRes.data
        await setScalperBotActive(state);
        await setDialogValues(settings.data);
        if(state === 'ENABLED') {
          setSnackbar({ open: true, message: `ScalperBot is active!`, severity: 'info' });
        }
      } catch (error) {
        console.error("Error fetching state:", error);
      }
    }
    fetchScalperBotState()
  }, []);

  const handleDialogChange = (e, field) => {
    setDialogValues({ ...dialogValues, [field]: e});
  }

  const handleDialogClose = () => setDialogOpen(false);
  const handleDialogOpen = () => setDialogOpen(true);

  const handleDialogSave = async () => {

    try{
      await postHelper(`${baseUrl}/settings`, dialogValues)
      setSnackbar({ open: true, message: "ScalperBot settings was updated! - You might need to restart bot for all changes to take effect", severity: 'success' });
      handleDialogClose()
    } catch (error) {
      console.error("Error setting settings:", error);
    }
  }

  const doDeal = async () => {
    try {
      const res = await postHelper(`${baseUrl}/scalper`, {strat: `scalper`})
      const { purchase: { success_response: { product_id } } } = res.data.data;
      setSnackbar({ open: true, message: `You successfully bought and sold ${product_id}!`, severity: 'success' });
    } catch (error) {
      const errorMsg = error.response?.data?.error || "Error when attempting to make the flip!";
      setSnackbar({ open: true, message: errorMsg, severity: 'error' });
    }
  };

  const scalperBot = async () => {
    try{
      // Toggle event bridge rule either on or off
      const bodyObject = { ruleName: "SCALPERBOT_RULE", newState: scalperBotActive === 'ENABLED' ? 'DISABLED' : 'ENABLED', newDuration: dialogValues.scalperBotTimer };
      const response = await postHelper(`${baseUrl}/botboss?rule=scalperbot`, bodyObject);
      const { data, message } = response;
      if (!message === 'success') {        
        throw new Error();
      }
      const { state } = data;
      await setScalperBotActive(state);
      const snackMessage = state === 'ENABLED' ? 'ScalperBot is active!' : 'ScalperBot is inactive!';
      // set snackbar message based on the state
      setSnackbar({ open: true, message: snackMessage, severity: 'success' });
    } catch (error) {
      console.error("Error doing deal:", error);
      setSnackbar({ open: true, message:"Failed to toggle ScalperBot state", severity: 'error' });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };
  
  const isDynamic = (value) => {
    return value === 'dynamicScalp' ? true : false;
  }

  return (
    <Box id='doRandomDeal' sx={{ p: 2 }}>
      <Button className='spaceUnder' variant="contained" color="primary" onClick={doDeal} disabled={scalperBotActive === 'DISABLED' || '' ? false : true}>
        Single Scalp
      </Button>
      <Button className='spaceAround' variant="contained" color="primary" onClick={scalperBot}>
        {`${scalperBotActive === 'DISABLED' ? 'Run' : 'Stop'} ScalperBot`}
      </Button>
      <Button variant="contained" color="secondary" onClick={handleDialogOpen}>
        <SettingsIcon />
      </Button>
      <Dialog open={dialogOpen} onClose={handleDialogClose}>
        <DialogTitle>Enter Numbers</DialogTitle>
        <DialogContent>
          <TextField
            label="Max Order Amount"
            type="number"
            fullWidth
            margin="normal"
            value={dialogValues.maxAmount}
            onChange={(e) => handleDialogChange(parseFloat(e.target.value), "maxAmount")}
          />
          <TextField
            label="Order value ($)"
            type="number"
            fullWidth
            margin="normal"
            value={parseFloat(dialogValues.buyPrice)}
            onChange={(e) => handleDialogChange(e.target.value, "buyPrice")}
          />
          <TextField
            label="Price Factor"
            type="number"
            fullWidth
            margin="normal"
            value={parseFloat(dialogValues.factor)}
            onChange={(e) => handleDialogChange(parseFloat(e.target.value), "factor")}
          />
          <FormControl fullWidth margin="normal">
            <InputLabel id="scalper-timer-label">ScalperBot Timer</InputLabel>
            <Select
              labelId="scalper-timer-label"
              value={dialogValues.scalperBotTimer}
              label="ScalperBot Timer"
              onChange={(e) => handleDialogChange(parseFloat(e.target.value), "scalperBotTimer")}
            >
              <MenuItem value={5}>5 minutes</MenuItem>
              <MenuItem value={10}>10 minutes</MenuItem>
              <MenuItem value={15}>15 minutes</MenuItem>
              <MenuItem value={30}>30 minutes</MenuItem>
              <MenuItem value={45}>45 minutes</MenuItem>
              <MenuItem value={60}>1 Hour</MenuItem>
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Switch
                checked={isDynamic(dialogValues.strat || false)}
                onChange={(e) => handleDialogChange(isDynamic(dialogValues.strat) ? 'stupidScalp' : 'dynamicScalp', "strat")}
                color="primary"
              />
            }
            label="Dynamic Mode"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleDialogSave} color="primary" variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
      <Divider />
      <div className='stratDetails'>
        <h2>ScalperBot</h2>
        <h3>Intro</h3>
        <p>This is a scalper bot</p>
        
        <h3>Theory</h3>
        <p>Analyze coins for potential to rise.</p>
        <p>Buy the coin and sell for 1% profit.</p>
        
        <h3>Pros/cons</h3>
        <h4>Pros</h4>
        <ul>
          <li>Due to the small margin and the Analyze calculation there is a high chance of turnover</li>
          <li>You pump the coinbase system evetually gaining lower fees</li>
        </ul>

        <h4>Cons</h4>
        <ul>
          <li>There is a risk of stale orders</li>
          <li>Margins are very small, thus it takes many orders to feel the gain</li>
        </ul>
      </div>
    </Box>
  );
}
